import React, { useEffect, useState } from "react";
import { api, TIMEOUTS } from "@/lib/api";
import { Sunrise, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

// The other half of the digest: that one is a daily email about the workspace,
// addressed to its admins. This is one DM per person about their own work.
const TIMEZONES = [
  "Europe/Bucharest", "Europe/London", "Europe/Berlin", "Europe/Madrid",
  "America/New_York", "America/Los_Angeles", "UTC",
];
// Label precomputed: an <option> with a mixed expression-and-text child gets
// wrapped by the dev instrumentation, and a <span> inside <option> is invalid.
const HOURS = Array.from({ length: 24 }, (_, h) => [h, `${String(h).padStart(2, "0")}:00`]);

export default function BriefingSettings({ workspaceId }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!workspaceId) return;
    api.get("/briefing/settings", { params: { workspace_id: workspaceId } })
      .then((r) => setCfg(r.data))
      .catch(() => setCfg(null));
  }, [workspaceId]);

  const patch = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/briefing/settings", payload,
        { params: { workspace_id: workspaceId } });
      setCfg(data);
      toast.success("Setări briefing salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    setPreview("");
    try {
      const { data } = await api.post("/briefing/send", null,
        { params: { workspace_id: workspaceId }, timeout: TIMEOUTS.digestSend });
      setPreview(data.briefing || data.note || "");
      toast.success(data.sent ? "Briefing trimis în DM-ul tău" : data.note);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trimiterea a eșuat");
    } finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="briefing-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Sunrise className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Briefing de dimineață</h3>
        <span className="label-tiny ml-2">// DM DE LA ATENA</span>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-xs text-fg-muted leading-relaxed">
          Fiecare membru primește dimineața un DM cu ce îl așteaptă: calendarul de azi,
          rândurile lui depășite sau cu termen azi de pe board-uri, tichetele care îl
          așteaptă și mențiunile din ultimele 24h. Cifrele sunt numărate din date, nu
          scrise de un model. <strong>Cine n-are nimic de raportat nu primește mesaj</strong> —
          un briefing gol în fiecare zi se ignoră exact în ziua în care conta.
        </p>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="briefing-enabled" checked={!!cfg.enabled} disabled={busy}
            onChange={(e) => patch({ enabled: e.target.checked })} className="accent-brand" />
          Trimite briefingul zilnic
        </label>

        <div className="flex flex-wrap gap-4">
          <label className="text-xs text-fg-muted space-y-1">
            <span className="block">Ora</span>
            <select data-testid="briefing-hour" value={cfg.hour} disabled={busy}
              onChange={(e) => patch({ hour: Number(e.target.value) })} className="input-flat text-sm py-1">
              {HOURS.map(([h, label]) => <option key={h} value={h}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs text-fg-muted space-y-1">
            <span className="block">Fus orar</span>
            <select data-testid="briefing-tz" value={cfg.timezone} disabled={busy}
              onChange={(e) => patch({ timezone: e.target.value })} className="input-flat text-sm py-1">
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </label>
          <label className="flex items-end gap-2 text-sm pb-1">
            <input type="checkbox" data-testid="briefing-weekdays" checked={!!cfg.weekdays_only} disabled={busy}
              onChange={(e) => patch({ weekdays_only: e.target.checked })} className="accent-brand" />
            Doar în zilele lucrătoare
          </label>
        </div>

        <button type="button" data-testid="briefing-send" onClick={sendNow} disabled={busy}
          className="text-xs px-3 py-2 border border-line/20 text-fg/70 hover:text-fg flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Trimite-mi briefingul acum
        </button>

        {preview && (
          <pre data-testid="briefing-preview"
            className="text-xs bg-bg border border-line/10 p-3 whitespace-pre-wrap leading-relaxed">{preview}</pre>
        )}
      </div>
    </section>
  );
}
